import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminListLocales, adminSetLocaleStatus, adminGenerateBase, adminAddLocale } from "@/lib/apiClient";
import { Globe, Wand2, Eye, EyeOff, Plus, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Translations — language list. One row per locale (the 20-language launch
 * catalog seeds automatically on backend boot; "Add locale" below is the
 * escape hatch for anything beyond that). Draft locales are admin-only
 * (visible here, invisible in the student-facing switcher — see
 * routes/i18n.py's /i18n/locales public endpoint); Published locales appear
 * in Settings' language picker immediately, no redeploy.
 *
 * A Translator hits this same page (see App.js route allow-lists), but
 * GET /admin/i18n/locales already filters `rows` down to just their own
 * assigned_locales server-side — nothing here re-implements that filtering.
 * This file only hides the buttons a Translator has no backend permission
 * to use anyway (Add locale, Generate, Publish/Unpublish), as a UX nicety
 * on top of the backend enforcement in routes/i18n.py.
 */
export default function AdminTranslations() {
  const { user } = useAuth();
  const isTranslator = user?.role === "translator";
  const [rows, setRows] = useState(null);
  const [busyCode, setBusyCode] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [sort, setSort] = useState({ col: "status", dir: "desc" });

  const toggleSort = (col) =>
    setSort((prev) => prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "status" ? "desc" : "asc" });

  const SortIcon = ({ col }) => {
    if (sort.col !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30" />;
    return sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const sorted = rows ? [...rows].sort((a, b) => {
    if (a.code === "en") return -1;
    if (b.code === "en") return 1;
    let cmp = 0;
    if (sort.col === "label") cmp = a.label.localeCompare(b.label);
    else if (sort.col === "status") cmp = (a.status === "published" ? 0 : 1) - (b.status === "published" ? 0 : 1);
    else if (sort.col === "reviewed") {
      const pa = a.total ? a.reviewed / a.total : 0;
      const pb = b.total ? b.reviewed / b.total : 0;
      cmp = pa - pb;
    }
    return sort.dir === "asc" ? cmp : -cmp;
  }) : null;

  const load = () => adminListLocales().then((d) => setRows(d.locales));
  useEffect(() => { load(); }, []);

  const toggleStatus = async (row) => {
    setBusyCode(row.code);
    try {
      const next = row.status === "published" ? "draft" : "published";
      await adminSetLocaleStatus(row.code, next);
      toast.success(next === "published" ? `${row.label} is now live for students` : `${row.label} moved back to draft`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setBusyCode(null); }
  };

  const generate = async (row) => {
    setBusyCode(row.code);
    try {
      const r = await adminGenerateBase(row.code);
      toast.success(r.translated > 0 ? `Machine-translated ${r.translated} new keys` : "Already up to date — nothing missing");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusyCode(null); }
  };

  const addLocale = async () => {
    if (!newCode.trim()) return;
    try {
      const r = await adminAddLocale({ code: newCode.trim().toLowerCase() });
      toast.success(r.already_existed ? "That locale already exists" : `Added ${r.locale.label} as draft`);
      setNewCode(""); setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add locale");
    }
  };

  if (!rows) return <div className="h-40 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="admin-translations">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-medium text-[#1F2937] flex items-center gap-2"><Globe className="w-4 h-4 text-[#D97706]" /> Languages</div>
          <p className="text-xs text-[#4B5563] mt-1">
            {isTranslator
              ? (rows.length === 1 ? "Your assigned language. Open it to review and edit translations." : "Your assigned languages. Open one to review and edit translations.")
              : "Draft locales are only visible here. Publish once reviewed to add them to students' language picker — no deploy needed."}
          </p>
        </div>
        {!isTranslator && (!adding ? (
          <button data-testid="i18n-add-locale-open" onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">
            <Plus className="w-3.5 h-3.5" /> Add locale
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input data-testid="i18n-add-locale-code" value={newCode} onChange={(e) => setNewCode(e.target.value)}
              placeholder="ISO code, e.g. 'am'" className="text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] focus:border-[#064E3B] outline-none w-40" />
            <button data-testid="i18n-add-locale-submit" onClick={addLocale} className="text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">Add</button>
            <button onClick={() => { setAdding(false); setNewCode(""); }} className="text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] text-[#4B5563] hover:border-[#064E3B]">Cancel</button>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E7E0D2] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E7E0D2] text-left text-xs uppercase tracking-wider text-[#4B5563]">
              <th className="px-4 py-3 font-medium">
                <button onClick={() => toggleSort("label")} className="inline-flex items-center gap-1 hover:text-[#064E3B] transition">
                  Language <SortIcon col="label" />
                </button>
              </th>
              <th className="px-4 py-3 font-medium">
                <button onClick={() => toggleSort("status")} className="inline-flex items-center gap-1 hover:text-[#064E3B] transition">
                  Status <SortIcon col="status" />
                </button>
              </th>
              <th className="px-4 py-3 font-medium">
                <button onClick={() => toggleSort("reviewed")} className="inline-flex items-center gap-1 hover:text-[#064E3B] transition">
                  Reviewed / Generated / Total <SortIcon col="reviewed" />
                </button>
              </th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const pct = r.total ? Math.round((100 * r.reviewed) / r.total) : 0;
              const busy = busyCode === r.code;
              return (
                <tr key={r.code} data-testid={`i18n-locale-row-${r.code}`} className="border-b border-[#E7E0D2] last:border-b-0 hover:bg-[#FDFBF7]">
                  <td className="px-4 py-3">
                    <Link to={`/admin/translations/${r.code}`} className="font-medium text-[#1F2937] hover:text-[#064E3B] hover:underline">
                      {r.label}
                    </Link>
                    <div className="text-xs text-[#4B5563]" dir={r.dir}>{r.native}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${
                      r.status === "published" ? "bg-[#ECFDF5] text-[#064E3B]" : "bg-[#F5EFE3] text-[#4B5563]"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 w-56">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden">
                        <div className="h-full bg-[#064E3B]" style={{ width: `${pct}%` }} />
                      </div>
                      <span
                        className="text-xs text-[#4B5563] tabular-nums shrink-0"
                        title={`Reviewed: ${r.reviewed} · Generated: ${r.generated} · Total: ${r.total}`}
                      >
                        {r.reviewed} / {r.generated} / {r.total}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {!isTranslator && r.code !== "en" && (
                        <button data-testid={`i18n-generate-${r.code}`} onClick={() => generate(r)} disabled={busy || !r.mt_configured}
                          title={r.mt_configured ? "Machine-translate missing keys" : "Set GOOGLE_TRANSLATE_API_KEY to enable"}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] disabled:opacity-40 disabled:cursor-not-allowed">
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Generate
                        </button>
                      )}
                      {!isTranslator && r.code !== "en" && (
                        <button data-testid={`i18n-toggle-status-${r.code}`} onClick={() => toggleStatus(r)} disabled={busy}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] disabled:opacity-40 disabled:cursor-not-allowed">
                          {r.status === "published" ? <><EyeOff className="w-3 h-3" /> Unpublish</> : <><Eye className="w-3 h-3" /> Publish</>}
                        </button>
                      )}
                      <Link to={`/admin/translations/${r.code}`} className="text-xs px-2.5 py-1 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">
                        Open
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
